import { Routes } from '@angular/router';
import { AuthGuard, LoginGuard, signedInGuard } from './core/guards/auth.guard';
import { lockedRouteGuard } from './core/guards/locked-route.guard';
import { BillingComponent } from './pages/billing/billing.component';

export const routes: Routes = [
  { path: '', redirectTo: 'login', pathMatch: 'full' },

  // ── PUBLIC ────────────────────────────────────────────────────────
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent),
    canActivate: [LoginGuard]
  },
  {
    path: 'set-password',
    loadComponent: () => import('./pages/set-password/set-password.component').then(m => m.SetPasswordComponent)
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./pages/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent)
  },
  {
    path: 'change-password',
    loadComponent: () => import('./pages/change-password/change-password.component').then(m => m.ChangePasswordComponent),
    // signed out → /login first; signed in but locked → lockedRouteGuard
    canActivate: [signedInGuard, lockedRouteGuard]
  },
  {
    path: 'queue/:hospitalCode',
    loadComponent: () => import('./pages/public-queue/public-queue.component').then(m => m.PublicQueueComponent)
  },
  {
    path: 'tele/:roomCode',
    loadComponent: () => import('./pages/telemedicine-room/telemedicine-room.component').then(m => m.TelemedicineRoomComponent),
    canActivate: [lockedRouteGuard]
  },
  {
    path: 'privacy',
    loadComponent: () => import('./pages/legal/legal.component').then(m => m.LegalComponent),
    data: { type: 'privacy' }
  },
  {
    path: 'terms',
    loadComponent: () => import('./pages/legal/legal.component').then(m => m.LegalComponent),
    data: { type: 'terms' }
  },

  // ── PROTECTED (all inside LayoutComponent) ────────────────────────
  {
    path: '',
    loadComponent: () => import('./shared/layout/layout.component').then(m => m.LayoutComponent),
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    children: [
      // ── Operational Dashboard — the new default (Session 4 PR3, May 2026) ──
      // The new command-center dashboard lives at /dashboard. The legacy
      // multi-tab /dashboard-old was retired in PR 18 (May 2026) after the
      // new dashboard ran in production without rollback for >1 cycle.
      {
        path: 'dashboard',
        loadComponent: () => import('./pages/operational-dashboard/operational-dashboard.component').then(m => m.OperationalDashboardComponent),
        data: { roles: ['HospitalAdmin', 'SuperAdmin'] }
      },
      // Old preview path — keep as alias to the new component for any
      // bookmarks still in the wild. Remove in a follow-up.
      { path: 'dashboard-new', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'face-scanner',
        loadComponent: () => import('./pages/face-scanner/face-scanner.component').then(m => m.FaceScannerComponent),
        data: { roles: ['Receptionist', 'HospitalAdmin', 'SuperAdmin', 'Nurse'] }
      },
      {
        path: 'accounts-receivable',
        loadComponent: () => import('./pages/receptionist-dashboard/receptionist-dashboard.component').then(m => m.ReceptionistDashboardComponent),
        data: { roles: ['Billing', 'Receptionist', 'HospitalAdmin', 'SuperAdmin'], arOnly: true }
      },
      {
        path: 'receptionist-dashboard',
        loadComponent: () => import('./pages/receptionist-dashboard/receptionist-dashboard.component').then(m => m.ReceptionistDashboardComponent),
        data: { roles: ['Receptionist', 'HospitalAdmin', 'SuperAdmin'] }
      },
      {
        path: 'all-patients',
        loadComponent: () => import('./pages/all-patients/all-patients.component').then(m => m.AllPatientsComponent),
        data: { roles: ['Receptionist', 'HospitalAdmin', 'SuperAdmin', 'Nurse', 'Doctor'] }
      },
      {
        path: 'active-visits',
        loadComponent: () => import('./pages/active-visits/active-visits.component').then(m => m.ActiveVisitsComponent),
        data: { roles: ['Receptionist', 'HospitalAdmin', 'SuperAdmin', 'Nurse'] }
      },
      {
        path: 'patient-register',
        loadComponent: () => import('./pages/patient-register/patient-register.component').then(m => m.PatientRegisterComponent),
        data: { roles: ['Receptionist', 'HospitalAdmin', 'SuperAdmin'] }
      },
      {
        path: 'patient-history/:id',
        loadComponent: () => import('./pages/patient-history/patient-history.component').then(m => m.PatientHistoryComponent),
        data: { roles: ['Doctor', 'HospitalAdmin', 'SuperAdmin', 'Receptionist', 'Nurse'] }
      },
      {
        path: 'health-timeline/:id',
        loadComponent: () => import('./pages/health-timeline/health-timeline.component').then(m => m.HealthTimelineComponent),
        data: { roles: ['Doctor', 'HospitalAdmin', 'SuperAdmin', 'Nurse'] }
      },
      {
        path: 'staff-management',
        loadComponent: () => import('./pages/staff-management/staff-management.component').then(m => m.StaffManagementComponent),
        data: { roles: ['HospitalAdmin', 'SuperAdmin'] }
      },
      {
        path: 'audit-log',
        loadComponent: () => import('./pages/audit-log/audit-log.component').then(m => m.AuditLogComponent),
        data: { roles: ['HospitalAdmin', 'SuperAdmin'] }
      },
      {
        path: 'doctor-dashboard',
        loadComponent: () => import('./pages/doctor-dashboard/doctor-dashboard.component').then(m => m.DoctorDashboardComponent),
        data: { roles: ['Doctor', 'HospitalAdmin', 'SuperAdmin'] }
      },
      {
        path: 'my-patients',
        loadComponent: () => import('./pages/my-patients/my-patients.component').then(m => m.MyPatientsComponent),
        data: { roles: ['Doctor', 'HospitalAdmin', 'SuperAdmin'] }
      },
      {
        path: 'lab-dashboard',
        loadComponent: () => import('./pages/lab-dashboard/lab-dashboard.component').then(m => m.LabDashboardComponent),
        // Radiology is retired and aliased to Lab — redirectAfterLogin sends Radiology here, so the
        // guard must admit it (otherwise login infinite-loops). Easy to revert when the dedicated
        // radiology-dashboard route is uncommented.
        data: { roles: ['Lab', 'Radiology', 'HospitalAdmin', 'SuperAdmin'] }
      },
      {
        path: 'pharmacy-dashboard',
        loadComponent: () => import('./pages/pharmacy-dashboard/pharmacy-dashboard.component').then(m => m.PharmacyDashboardComponent),
        data: { roles: ['Pharmacy', 'HospitalAdmin', 'SuperAdmin'] }
      },
      {
        path: 'nurse-dashboard',
        loadComponent: () => import('./pages/nurse-dashboard/nurse-dashboard.component').then(m => m.NurseDashboardComponent),
        data: { roles: ['Nurse', 'HospitalAdmin', 'SuperAdmin'] }
      },
      {
        path: 'ambulance-dashboard',
        loadComponent: () => import('./pages/ambulance-dashboard/ambulance-dashboard.component').then(m => m.AmbulanceDashboardComponent),
        data: { roles: ['Ambulance', 'HospitalAdmin', 'SuperAdmin', 'Receptionist'] }
      },
      {
        path: 'queue-board',
        loadComponent: () => import('./pages/queue-board/queue-board.component').then(m => m.QueueBoardComponent),
        data: { roles: ['HospitalAdmin', 'SuperAdmin', 'Receptionist', 'Nurse', 'Doctor'] }
      },
      {
        path: 'discharge-summary/:visitId',
        loadComponent: () => import('./pages/discharge-summary/discharge-summary.component').then(m => m.DischargeSummaryComponent),
        data: { roles: ['Doctor', 'HospitalAdmin', 'SuperAdmin', 'Receptionist', 'Billing', 'Pharmacy', 'Nurse', 'Lab', 'Radiology'] }
      },
      // ── Analytics + Reports REMOVED (Session 4 follow-up, May 2026) ─
      // Per user request after the operational-dashboard redesign:
      // the new /dashboard absorbs the operational signal those two
      // pages used to carry. Kept as redirects so old bookmarks and
      // sidebar shortcuts that may exist in users' browsers don't 404.
      { path: 'analytics', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'hospital-tests',
        loadComponent: () => import('./pages/hospital-test-management/hospital-test-management.component').then(m => m.HospitalTestManagementComponent),
        data: { roles: ['HospitalAdmin', 'SuperAdmin'] }
      },
      {
        path: 'bed-management',
        loadComponent: () => import('./pages/bed-management/bed-management.component').then(m => m.BedManagementComponent),
        data: { roles: ['HospitalAdmin', 'SuperAdmin', 'Nurse', 'Receptionist', 'Doctor'] }
      },
      {
        path: 'billing-counter',
        loadComponent: () => import('./pages/billing-counter/billing-counter.component').then(m => m.BillingCounterComponent),
        data: { roles: ['Billing', 'Receptionist', 'HospitalAdmin', 'SuperAdmin'] }
      },
      {
        path: 'payment-audit',
        loadComponent: () => import('./pages/payment-audit/payment-audit.component').then(m => m.PaymentAuditComponent),
        data: { roles: ['Billing', 'HospitalAdmin', 'SuperAdmin'] }
      },
      {
        path: 'ip-billing',
        loadComponent: () => import('./pages/ip-billing/ip-billing.component').then(m => m.IPBillingComponent),
        data: { roles: ['Receptionist', 'HospitalAdmin', 'SuperAdmin', 'Doctor', 'Nurse', 'Billing'] }
      },
      // RADIOLOGY HIDDEN (not in use — all tests go through Lab). Re-enable by uncommenting.
      // {
      //   path: 'radiology-dashboard',
      //   loadComponent: () => import('./pages/radiology-dashboard/radiology-dashboard.component').then(m => m.RadiologyDashboardComponent),
      //   data: { roles: ['Radiology', 'HospitalAdmin', 'SuperAdmin'] }
      // },
      {
        path: 'medicine-stock',
        loadComponent: () => import('./pages/medicine-stock/medicine-stock.component').then(m => m.MedicineStockComponent),
        data: { roles: ['Pharmacy', 'HospitalAdmin', 'SuperAdmin'] }
      },
      { path: 'reports', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'leave-management',
        loadComponent: () => import('./pages/leave-management/leave-management.component').then(m => m.LeaveManagementComponent),
        data: { roles: ['HospitalAdmin', 'SuperAdmin', 'Doctor', 'Nurse', 'Receptionist', 'Lab', 'Pharmacy', 'Billing', 'Radiology', 'Ambulance'] }
      },
      {
        path: 'staff-directory',
        loadComponent: () => import('./pages/staff-directory/staff-directory.component').then(m => m.StaffDirectoryComponent),
        data: { roles: ['HospitalAdmin', 'SuperAdmin', 'Doctor', 'Nurse', 'Receptionist', 'Lab', 'Pharmacy', 'Billing', 'Radiology', 'Ambulance'] }
      },
      // emergency-dashboard route removed (Session 2, May 2026) — module dropped per request.
      {
        path: 'settings',
        loadComponent: () => import('./pages/settings/settings.component').then(m => m.SettingsComponent),
        data: { roles: ['HospitalAdmin', 'SuperAdmin'] }
      },
      {
        path: 'onboarding',
        loadComponent: () => import('./pages/onboarding/onboarding.component').then(m => m.OnboardingComponent),
        data: { roles: ['HospitalAdmin', 'SuperAdmin'] }
      },
      {
        path: 'active-sessions',
        loadComponent: () => import('./pages/active-sessions/active-sessions.component').then(m => m.ActiveSessionsComponent),
        data: { roles: ['HospitalAdmin', 'SuperAdmin'] }
      },
      {
        path: 'pending-refunds',
        loadComponent: () => import('./pages/pending-refunds/pending-refunds.component').then(m => m.PendingRefundsComponent),
        data: { roles: ['HospitalAdmin', 'SuperAdmin', 'Billing', 'Receptionist'] }
      },
      {
        path: 'lab-cancellation-requests',
        loadComponent: () => import('./pages/lab-cancellation-requests/lab-cancellation-requests.component').then(m => m.LabCancellationRequestsComponent),
        // Radiology is aliased to Lab (retired role → Lab dashboard); the RefundsController
        // lab-confirm + list endpoints now admit Radiology too, so this page works for it.
        data: { roles: ['Lab', 'Radiology', 'HospitalAdmin', 'SuperAdmin'] }
      },
      {
        path: 'super-admin',
        loadComponent: () => import('./pages/super-admin/super-admin.component').then(m => m.SuperAdminComponent),
        data: { roles: ['SuperAdmin'] }
      },
      {
        path: 'platform-security',
        loadComponent: () => import('./pages/platform-security/platform-security.component').then(m => m.PlatformSecurityComponent),
        data: { roles: ['SuperAdmin'] }
      },
      {
        path: 'platform-announcements',
        loadComponent: () => import('./pages/platform-announcements/platform-announcements.component').then(m => m.PlatformAnnouncementsComponent),
        data: { roles: ['SuperAdmin'] }
      },
      {
        // User journeys (2026-09-29): what a staff member did, step by step, when something broke.
        path: 'platform-journeys',
        loadComponent: () => import('./pages/platform-journeys/platform-journeys.component').then(m => m.PlatformJourneysComponent),
        data: { roles: ['SuperAdmin'] }
      },
      {
        // Hospital feedback/ratings about MediTrack (2026-10-11).
        path: 'platform-feedback',
        loadComponent: () => import('./pages/platform-feedback/platform-feedback.component').then(m => m.PlatformFeedbackComponent),
        data: { roles: ['SuperAdmin'] }
      },
      {
        path: 'support-access',
        loadComponent: () => import('./pages/support-access/support-access.component').then(m => m.SupportAccessComponent),
        data: { roles: ['HospitalAdmin'] }
      },
      {
        path: 'super-admin/hospitals/:id',
        loadComponent: () => import('./pages/hospital-console/hospital-console.component').then(m => m.HospitalConsoleComponent),
        data: { roles: ['SuperAdmin'] }
      },
      {
        path: 'billing-dashboard',
        loadComponent: () => import('./pages/billing-dashboard/billing-dashboard.component').then(m => m.BillingDashboardComponent),
        data: { roles: ['SuperAdmin'] }
      },
      {
        path: 'my-subscription',
        loadComponent: () => import('./pages/my-subscription/my-subscription.component').then(m => m.MySubscriptionComponent),
        data: { roles: ['HospitalAdmin'] }
      },
      { path: 'owner-dashboard', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'insurance-claims',
        loadComponent: () => import('./pages/insurance-claims/insurance-claims.component').then(m => m.InsuranceClaimsComponent),
        data: { roles: ['Receptionist', 'HospitalAdmin', 'SuperAdmin', 'Billing'] }
      },
      {
        path: 'billing/invoice/:billId',
        component: BillingComponent,
        canActivate: [AuthGuard],
        // AUDIT FIX (2026-06-16): added 'Billing' — the role that owns invoicing couldn't open the invoice it generates.
        // Owner decision B1 (2026-11-01): invoices are billing-only — 'Doctor' dropped to match the API (nothing links here for doctors).
        data: { roles: ['Receptionist', 'Billing', 'HospitalAdmin', 'SuperAdmin'] }
      },
    ]
  },

  { path: '**', redirectTo: 'login' }
];
